import type { Aggregator, Customer, Ticket, TicketStatus, ServiceOffering } from '../../domain/types'
import { PRESETS, LOCATION, ATTENDANT_BY_NAME } from '../../domain/catalog'
import { offeringsForService, findOffering } from '../../domain/queues'

// ============================================================
// Clientes (entidade atendida — exibida conforme o vertical)
// ============================================================
export const CLIENTES: Customer[] = [
  { id: 1, nome: 'João Aldo Pereira',     cpf: '182.553.940-12', telefone: '(11) 98842-1190', email: 'joao.aldo@email.com' },
  { id: 2, nome: 'Mariana Costa Ribeiro', cpf: '405.118.220-77', telefone: '(11) 99713-4420', email: 'mari.costa@email.com' },
  { id: 3, nome: 'Rafael Nunes Souza',    cpf: '338.902.165-30', telefone: '(11) 98120-7781', email: 'rafael.ns@email.com' },
  { id: 4, nome: 'Beatriz Lima Andrade',  cpf: '271.640.815-09', telefone: '(11) 99664-2025', email: 'bia.lima@email.com' },
  { id: 5, nome: 'Carlos Eduardo Pinto',  cpf: '509.337.114-58', telefone: '(11) 98455-9013', email: 'cadu.pinto@email.com' },
  { id: 6, nome: 'Patrícia Gomes Alves',  cpf: '144.728.503-91', telefone: '(11) 99201-6634', email: 'paty.gomes@email.com' },
  { id: 7, nome: 'Sandra Rocha Dias',     cpf: '627.815.490-22', telefone: '(11) 98390-5567', email: 'sandra.rd@email.com' },
  { id: 8, nome: 'Diego Martins Faria',   cpf: '318.225.706-44', telefone: '(11) 99078-1102', email: 'diego.mf@email.com' },
]

// ============================================================
// Construtores
// ============================================================
let _tid = 5000
const _codeCount: Record<number, number> = {} // contador por FILA (serviceConfigId)

interface TicketExtra { resource?: string | null; iniciadoEm?: string | null; concluidoEm?: string | null }

// Cria um ticket a partir de uma OFERTA resolvida (serviço+fila+sessão).
// code/sala vêm da fila; estMin do serviço.
export function mkTicket(off: ServiceOffering, sequence: number, status: TicketStatus, extra: TicketExtra = {}): Ticket {
  _codeCount[off.serviceConfigId] = (_codeCount[off.serviceConfigId] || 0) + 1
  const wasCalled = status === 'Called' || status === 'Started' || status === 'Completed'
  return {
    ticketId: ++_tid,
    codigoTicket: off.code + String(_codeCount[off.serviceConfigId]).padStart(3, '0'),
    serviceId: off.serviceId,
    serviceConfigId: off.serviceConfigId,
    sessionId: off.sessionId,
    nomeServico: off.serviceName,
    sala: off.sala,
    estMin: off.estMin,
    sequence,
    status,
    attendanceType: 'PRESENTIAL',
    accessKey: null,
    resourceId: extra.resource ? (ATTENDANT_BY_NAME[extra.resource]?.id ?? null) : null,
    resourceNome: extra.resource ? (ATTENDANT_BY_NAME[extra.resource]?.nome ?? extra.resource) : null,
    iniciadoEm: extra.iniciadoEm ?? null,
    concluidoEm: extra.concluidoEm ?? null,
    timesCalled: wasCalled ? 1 : 0,
    lastTimeCalled: wasCalled ? (extra.iniciadoEm ?? null) : null,
  }
}

// Gera o próximo código de bilhete numa fila (usado ao transferir para outra fila).
export function nextCodigo(off: ServiceOffering): string {
  _codeCount[off.serviceConfigId] = (_codeCount[off.serviceConfigId] || 0) + 1
  return off.code + String(_codeCount[off.serviceConfigId]).padStart(3, '0')
}

let _aggSeq = 0
export function aggId(hhmmss: string): string {
  _aggSeq++
  return `AGG-20250608${hhmmss}-${(0xa1b2c3 + _aggSeq * 977).toString(16).slice(0, 8)}`
}

/**
 * Código curto compartilhado por todas as etapas da jornada (ex.: "J-0023").
 * Impresso no bilhete de jornada para representar o atendimento inteiro,
 * distinto do `codigoTicket` de cada bilhete individual.
 */
export function friendlyCode(seq: number): string {
  return `J-${String(seq).padStart(4, '0')}`
}

interface MkAggInput {
  cliente: Customer
  templateKey: string | null
  criadoEm: string
  prioridade?: number
  fields: Record<string, string>
  tickets: Ticket[]
}
export function mkAgg({ cliente, templateKey, criadoEm, prioridade = 0, fields, tickets }: MkAggInput): Aggregator {
  const cleanFields: Record<string, string> = {}
  for (const [k, v] of Object.entries(fields)) {if (v && v.trim() !== '' && v !== '—') {cleanFields[k] = v}}
  return {
    aggregatorId: aggId(criadoEm.replace('h', '').replace(':', '') + '00'),
    friendlyCode: friendlyCode(_aggSeq),
    status: tickets.every((t) => t.status === 'Canceled') ? 'Canceled'
      : tickets.every((t) => t.status === 'Completed' || t.status === 'Canceled')
        ? (tickets.some((t) => t.status === 'Canceled') ? 'PartiallyCompleted' : 'Completed')
        : tickets.some((t) => t.status === 'Called' || t.status === 'Started') ? 'InProgress' : 'Waiting',
    criadoEm,
    cliente,
    localizacao: LOCATION,
    prioridade,
    tipoAtendimento: 'PRESENTIAL',
    metadata: { templateKey, fields: cleanFields },
    tickets,
    _createdAtMs: Date.now(),
  }
}

interface PresetState { s: TicketStatus; r?: string; ini?: string; fim?: string; sess?: number }
function fromPreset(presetKey: string, states: PresetState[]): Ticket[] {
  return PRESETS[presetKey].itens.map((it, i) => {
    const st = states[i]
    // resolve a oferta default do serviço (preset não-ambíguo); `sess` força uma sessão
    // específica da fila (ex.: pôr a Triagem deste cliente na sessão da Tarde).
    const off = (st.sess != null
        ? offeringsForService(it.serviceId).find((o) => o.sessionId === st.sess)
        : findOffering(it.serviceId, it.serviceConfigId, it.sessionId))
      ?? offeringsForService(it.serviceId)[0]
    return mkTicket(off, it.sequence, st.s, { resource: st.r, iniciadoEm: st.ini, concluidoEm: st.fim })
  })
}

// — Seeds —
export const SEED_AGGREGATORS: Aggregator[] = [
  // 1) admissional no nível 2 (clínico em atendimento, audio/vista bloqueados)
  mkAgg({
    cliente: CLIENTES[0], templateKey: 'admissional', criadoEm: '08h12', prioridade: 0,
    fields: { empresa: 'ACME Metalúrgica', funcao: 'Soldador' },
    tickets: fromPreset('admissional', [
      { s: 'Completed', r: 'Téc. Camila Souza',   ini: '08h12', fim: '08h18' },
      { s: 'Completed', r: 'Enf. Paulo Reis',     ini: '08h20', fim: '08h31' },
      { s: 'Started',   r: 'Dra. Helena Marques', ini: '08h36' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
  // 2) periódico — recepção feita, triagem disponível para chamar
  mkAgg({
    cliente: CLIENTES[1], templateKey: 'periodico', criadoEm: '08h44', prioridade: 1,
    fields: { empresa: 'Transportes Sul', funcao: 'Motorista' },
    tickets: fromPreset('periodico', [
      { s: 'Completed', r: 'Téc. Camila Souza', ini: '08h44', fim: '08h49' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
  // 3) admissional — exames liberados, audiometria CHAMADA, vista bloqueada por concorrente
  mkAgg({
    cliente: CLIENTES[2], templateKey: 'admissional', criadoEm: '09h02',
    fields: { empresa: 'ACME Metalúrgica', funcao: 'Operador de empilhadeira' },
    tickets: fromPreset('admissional', [
      { s: 'Completed', r: 'Téc. Camila Souza',   ini: '09h02', fim: '09h07' },
      { s: 'Completed', r: 'Enf. Paulo Reis',     ini: '09h10', fim: '09h19' },
      { s: 'Completed', r: 'Dra. Helena Marques', ini: '09h22', fim: '09h38' },
      { s: 'Called',    r: 'Enf. Rita Mota' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
  // 4) admissional recém-criado — só recepção disponível
  mkAgg({
    cliente: CLIENTES[3], templateKey: 'admissional', criadoEm: '09h25',
    fields: { empresa: 'Construtora Norte', funcao: 'Pintora' },
    tickets: fromPreset('admissional', [
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
  // 5) demissional concluído
  mkAgg({
    cliente: CLIENTES[4], templateKey: 'demissional', criadoEm: '07h50',
    fields: { empresa: 'Frigorífico Central', funcao: 'Açougueiro' },
    tickets: fromPreset('demissional', [
      { s: 'Completed', r: 'Téc. Camila Souza',   ini: '07h50', fim: '07h55' },
      { s: 'Completed', r: 'Dr. André Lopes',     ini: '07h58', fim: '08h12' },
      { s: 'Completed', r: 'Dra. Helena Marques', ini: '08h15', fim: '08h21' },
    ]),
  }),
  // 6) periódico parcialmente completo — audiometria cancelada
  mkAgg({
    cliente: CLIENTES[5], templateKey: 'periodico', criadoEm: '08h05',
    fields: { empresa: 'Têxtil Brasil', funcao: 'Costureira' },
    tickets: fromPreset('periodico', [
      { s: 'Completed', r: 'Téc. Camila Souza',   ini: '08h05', fim: '08h10' },
      { s: 'Completed', r: 'Enf. Paulo Reis',     ini: '08h12', fim: '08h20' },
      { s: 'Completed', r: 'Dr. André Lopes',     ini: '08h24', fim: '08h38' },
      { s: 'Canceled' },
      { s: 'Completed', r: 'Dra. Helena Marques', ini: '08h42', fim: '08h47' },
    ]),
  }),
  // 7) admissional — triagem disponível na sessão da TARDE (demonstra fila+sessão)
  mkAgg({
    cliente: CLIENTES[6], templateKey: 'admissional', criadoEm: '09h31',
    fields: { empresa: 'Logística Vale', funcao: 'Conferente' },
    tickets: fromPreset('admissional', [
      { s: 'Completed', r: 'Téc. Camila Souza', ini: '09h31', fim: '09h36' },
      { s: 'Opened', sess: 2139003 }, { s: 'Opened' }, { s: 'Opened' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
  // 8) periódico — triagem EM ATENDIMENTO
  mkAgg({
    cliente: CLIENTES[7], templateKey: 'periodico', criadoEm: '09h12',
    fields: { empresa: 'Transportes Sul', funcao: 'Motorista' },
    tickets: fromPreset('periodico', [
      { s: 'Completed', r: 'Téc. Camila Souza', ini: '09h12', fim: '09h17' },
      { s: 'Started',   r: 'Enf. Paulo Reis',   ini: '09h40' },
      { s: 'Opened' }, { s: 'Opened' }, { s: 'Opened' },
    ]),
  }),
]

// Estampa _startedAt nos tickets já iniciados no seed (para o cronômetro). (era seedClock em app.jsx)
export function seedClock(aggs: Aggregator[]): Aggregator[] {
  return aggs.map((a) => ({
    ...a,
    tickets: a.tickets.map((t) =>
      t.status === 'Started'
        ? { ...t, _startedAt: Date.now() - (6 + Math.floor(Math.random() * 14)) * 60000 }
        : t
    ),
  }))
}
